import { useAuth } from '@/contexts/AuthContext';

interface AdminPermission {
  system: {
    view: boolean;
    manage: boolean;
  };
  organizations: {
    view: boolean;
    manage: boolean;
    create: boolean;
    delete: boolean;
  };
  users: {
    view: boolean;
    manage: boolean;
    create: boolean;
    delete: boolean;
  };
  analytics: {
    view: boolean;
    export: boolean;
  };
  audit: {
    view: boolean;
    export: boolean;
  };
  permissions: {
    view: boolean;
    manage: boolean;
  };
  database: {
    view: boolean;
    manage: boolean;
    backup: boolean;
  };
  imports: {
    view: boolean;
    manage: boolean;
  };
  settings: {
    view: boolean;
    manage: boolean;
  };
}

const adminPermissionMap: Record<string, keyof AdminPermission | string> = {
  'admin.system.view': 'system.view',
  'admin.system.manage': 'system.manage',
  'admin.organizations.view': 'organizations.view',
  'admin.organizations.manage': 'organizations.manage',
  'admin.organizations.create': 'organizations.create',
  'admin.organizations.delete': 'organizations.delete',
  'admin.users.view': 'users.view',
  'admin.users.manage': 'users.manage',
  'admin.users.create': 'users.create',
  'admin.users.delete': 'users.delete',
  'admin.analytics.view': 'analytics.view',
  'admin.analytics.export': 'analytics.export',
  'admin.audit.view': 'audit.view',
  'admin.audit.export': 'audit.export',
  'admin.permissions.view': 'permissions.view',
  'admin.permissions.manage': 'permissions.manage',
  'admin.database.view': 'database.view',
  'admin.database.manage': 'database.manage',
  'admin.database.backup': 'database.backup',
  'admin.imports.view': 'imports.view',
  'admin.imports.manage': 'imports.manage',
  'admin.settings.view': 'settings.view',
  'admin.settings.manage': 'settings.manage',
};

export function useAdminPermissions() {
  const { user, checkPermission } = useAuth();

  const hasPermission = (permission: string): boolean => {
    // For system admin permissions, check Django staff status
    if (permission.startsWith('admin.') && user?.is_staff) {
      return true;
    }

    // For all other permissions, use the standard organization-based permission check
    return checkPermission(permission);
  };

  const getAdminPermissions = (): AdminPermission => {
    // Only Django staff users have system admin permissions
    if (user?.is_staff) {
      // Staff users have all system admin permissions
      return {
        system: { view: true, manage: true },
        organizations: { view: true, manage: true, create: true, delete: true },
        users: { view: true, manage: true, create: true, delete: true },
        analytics: { view: true, export: true },
        audit: { view: true, export: true },
        permissions: { view: true, manage: true },
        database: { view: true, manage: true, backup: true },
        imports: { view: true, manage: true },
        settings: { view: true, manage: true },
      };
    }

    // For other users, check individual permissions
    return {
      system: {
        view: hasPermission('admin.system.view'),
        manage: hasPermission('admin.system.manage'),
      },
      organizations: {
        view: hasPermission('admin.organizations.view'),
        manage: hasPermission('admin.organizations.manage'),
        create: hasPermission('admin.organizations.create'),
        delete: hasPermission('admin.organizations.delete'),
      },
      users: {
        view: hasPermission('admin.users.view'),
        manage: hasPermission('admin.users.manage'),
        create: hasPermission('admin.users.create'),
        delete: hasPermission('admin.users.delete'),
      },
      analytics: {
        view: hasPermission('admin.analytics.view'),
        export: hasPermission('admin.analytics.export'),
      },
      audit: {
        view: hasPermission('admin.audit.view'),
        export: hasPermission('admin.audit.export'),
      },
      permissions: {
        view: hasPermission('admin.permissions.view'),
        manage: hasPermission('admin.permissions.manage'),
      },
      database: {
        view: hasPermission('admin.database.view'),
        manage: hasPermission('admin.database.manage'),
        backup: hasPermission('admin.database.backup'),
      },
      imports: {
        view: hasPermission('admin.imports.view'),
        manage: hasPermission('admin.imports.manage'),
      },
      settings: {
        view: hasPermission('admin.settings.view'),
        manage: hasPermission('admin.settings.manage'),
      },
    };
  };

  // Separate system admin from organization admin
  const isSystemAdmin = user?.is_staff || false;
  const isOrgAdmin = user?.role === 'admin' || false;

  return {
    hasPermission,
    getAdminPermissions,
    isSystemAdmin,
    isOrgAdmin,
    // Legacy compatibility
    isAdmin: isSystemAdmin,
    user,
  };
}
